/**
 * Gates creation flows via `VITE_GENERAL_FEATURE_*` env vars (build-time).
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import type { GeneralFeature } from '@/lib/general-features-flag';
import {
  notifyGeneralFeatureUnavailable,
  readGeneralFeatureFlagsFromImportMeta,
} from '@/lib/general-features-flag';

interface GeneralFeatureFlagsContextValue {
  flags: Record<GeneralFeature, boolean>;
  /** True when the capability is allowed. */
  isEnabled: (feature: GeneralFeature) => boolean;
  /** Toast + return false when disabled. */
  ensureEnabled: (feature: GeneralFeature) => boolean;
}

const GeneralFeatureFlagsContext =
  createContext<GeneralFeatureFlagsContextValue | null>(null);

export function GeneralFeatureFlagsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const flags = useMemo(
    () => readGeneralFeatureFlagsFromImportMeta(import.meta.env),
    []
  );

  const isEnabled = useCallback(
    (feature: GeneralFeature): boolean => flags[feature] !== false,
    [flags]
  );

  const ensureEnabled = useCallback(
    (feature: GeneralFeature): boolean => {
      if (isEnabled(feature)) return true;
      notifyGeneralFeatureUnavailable();
      return false;
    },
    [isEnabled]
  );

  const value = useMemo<GeneralFeatureFlagsContextValue>(
    () => ({
      flags,
      isEnabled,
      ensureEnabled,
    }),
    [flags, isEnabled, ensureEnabled]
  );

  return (
    <GeneralFeatureFlagsContext.Provider value={value}>
      {children}
    </GeneralFeatureFlagsContext.Provider>
  );
}

export function useGeneralFeatureFlags(): GeneralFeatureFlagsContextValue {
  const ctx = useContext(GeneralFeatureFlagsContext);
  if (!ctx) {
    throw new Error(
      'useGeneralFeatureFlags must be used within GeneralFeatureFlagsProvider'
    );
  }
  return ctx;
}
